/**
 * AI Flashcard Service - Sử dụng OpenAI để tạo flashcards từ PDF content
 * CHỈ tạo flashcards dựa trên nội dung được cung cấp
 */

import { aiHeaders, handleAIError } from '../ai/aiConfig'
import type { FlashcardDraft, GenerateFlashcardsRequest, GenerateFlashcardsResponse } from '../../types/flashcard'
import { flashcardPromptConfig } from '../../config/flashcard-prompt.config'
import { numberToDifficulty } from '../../types/flashcard'
import { API_CONFIG } from '../../config/api.config'

interface FlashcardAIResponse {
  cards: Array<{
    id?: number // ID từ AI (optional)
    front: string
    back: string
    difficulty: 1 | 2 | 3
    source: string
  }>
}

class AIFlashcardService {
  private model: string = 'gpt-4o-mini'
  private temperature: number = 0.3 // Thấp hơn để consistent hơn

  constructor() {
    // API key được quản lý bởi proxy - không cần trong client
    const proxyUrl = API_CONFIG.openai.proxyUrl
    if (!proxyUrl) {
      console.warn('⚠️ CẢNH BÁO: OpenAI Proxy URL chưa được cấu hình!')
    }
  }

  /**
   * Generate flashcards từ PDF content
   */
  async generateFlashcards(request: GenerateFlashcardsRequest): Promise<GenerateFlashcardsResponse> {
    try {
      // Kiểm tra proxy URL
      const proxyUrl = API_CONFIG.openai.proxyUrl
      if (!proxyUrl) {
        return {
          success: false,
          error: 'OpenAI Proxy chưa được cấu hình. Vui lòng liên hệ admin.'
        }
      }

      const { content, keywords = [], count = 15 } = request

      // Validate content
      if (!content || content.length < 100) {
        return {
          success: false,
          error: 'Nội dung tài liệu quá ngắn (tối thiểu 100 ký tự)'
        }
      }

      console.log('🤖 Đang tạo flashcards từ AI...')
      console.log('📝 Content length:', content.length, 'characters')
      console.log('🏷️ Keywords:', keywords.join(', '))
      console.log('🔐 Using proxy:', proxyUrl)

      // Create prompt
      const prompt = this.createFlashcardPrompt(content, keywords, count)

      // Call OpenAI API qua proxy để bảo mật
      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...aiHeaders('text')
        },
        body: JSON.stringify({
          model: this.model,
          messages: [
            {
              role: 'system',
              content: flashcardPromptConfig.systemPrompt // Từ config
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: this.temperature,
          max_tokens: 3000,
          response_format: { type: "json_object" }
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(`API Error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`)
      }

      const data = await response.json()
      const aiResponse = data.choices?.[0]?.message?.content || '{}'

      // Parse AI response
      const parsedResponse: FlashcardAIResponse = JSON.parse(aiResponse)

      if (!parsedResponse.cards || !Array.isArray(parsedResponse.cards)) {
        throw new Error('AI response không đúng định dạng')
      }

      // Convert to FlashcardDraft
      const drafts: FlashcardDraft[] = parsedResponse.cards.map((card, index) => ({
        id: `draft_${Date.now()}_${index}`,
        userId: 'default_user', // TODO: Get from auth context
        documentId: request.documentId,
        documentName: request.documentName,
        front: card.front,
        back: card.back,
        tags: keywords,
        difficulty: numberToDifficulty(card.difficulty || 1),
        isEdited: false,
        source: card.source || ''
      }))

      // Sắp xếp flashcards từ dễ đến khó
      drafts.sort((a, b) => {
        const order = { 'easy': 1, 'medium': 2, 'hard': 3 }
        return order[a.difficulty] - order[b.difficulty]
      })

      console.log('✅ Generated', drafts.length, 'flashcard drafts (sorted by difficulty)')
      console.log('📊 Distribution: Easy=' + drafts.filter(d => d.difficulty === 'easy').length +
        ', Medium=' + drafts.filter(d => d.difficulty === 'medium').length +
        ', Hard=' + drafts.filter(d => d.difficulty === 'hard').length)

      return {
        success: true,
        drafts,
        tokensUsed: data.usage?.total_tokens || 0
      }

    } catch (error) {
      console.error('❌ Error generating flashcards:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Có lỗi xảy ra khi tạo flashcards'
      }
    }
  }

  /**
   * Create prompt cho flashcard generation
   * Sử dụng config từ flashcard-prompt.config.ts
   */
  private createFlashcardPrompt(content: string, keywords: string[], count: number): string {
    // Chunk content nếu quá dài (max ~6000 chars để fit trong token limit)
    let contentToUse = content
    if (content.length > 6000) {
      contentToUse = content.substring(0, 6000) + '...\n[Nội dung còn lại đã được cắt bớt]'
      console.log('⚠️ Content quá dài, đã cắt xuống còn 6000 ký tự')
    }

    // Sử dụng prompt từ config file (có thể tinh chỉnh dễ dàng)
    return flashcardPromptConfig.mainPrompt({
      count,
      keywords,
      content: contentToUse
    })
  }

  /**
   * Regenerate một flashcard cụ thể
   * TODO: Refactor to use proxy
   */
  async regenerateFlashcard(
    draft: FlashcardDraft,
    content: string,
    instruction?: string
  ): Promise<FlashcardDraft | null> {
    console.warn('⚠️ regenerateFlashcard: Feature temporarily disabled - needs proxy implementation')
    return null

    /* TODO: Implement with proxy
    try {
      const proxyUrl = import.meta.env.VITE_OPENAI_PROXY_URL
      if (!proxyUrl) throw new Error('Proxy not configured')
      
      const prompt = `Tạo lại flashcard...`
      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model: this.model, messages: [...] })
      })
      // ... rest of implementation
    } catch (error) {
      return null
    }
    */
  }

  /**
   * Test connection to OpenAI
   * TODO: Refactor to use proxy
   */
  async testConnection(): Promise<boolean> {
    console.warn('⚠️ testConnection: Feature temporarily disabled - needs proxy implementation')
    return false

    /* TODO: Implement with proxy
    try {
      const proxyUrl = import.meta.env.VITE_OPENAI_PROXY_URL
      if (!proxyUrl) return false
      // Test with simple request
      return true
    } catch {
      return false
    }
    */
  }
}

// Export singleton instance
export const aiFlashcardService = new AIFlashcardService()
export default aiFlashcardService

